import React, { useEffect } from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';
import {
  Body,
  Button,
  Headline,
  Icon,
  LinkButton,
  SubPageNav,
} from '@sato/components';
import { observer } from 'mobx-react-lite';

import { Link } from '@components/common/Link';
import { ROUTES } from '@routes/routes.constants';
import { authStore } from '@stores/authStore';
import { localizationStore } from '@stores/localization';
import { isLocalOrTestEnv } from '@utils/featureFlags';

/**
 * An error boundary view to be used with `react-router-dom` errorElement
 * https://reactrouter.com/en/main/route/error-element
 */
export const ErrorBoundaryElement = observer(() => {
  const t = localizationStore.getTranslation;

  const navigate = useNavigate();

  const error = useRouteError();

  useEffect(() => {
    if (isLocalOrTestEnv) {
      console.error(error);
    }
  }, []);

  return (
    <>
      <SubPageNav />

      <div className="flex flex-col items-center justify-center gap-3 py-8 container-margin-wide">
        <Icon
          icon="warning"
          className="pb-3 text-[6rem] text-black-text lg:text-[8rem]"
        />

        <Headline level={2}>{t('error.error_title')}</Headline>

        <Body variant="long" className="mb-3 text-center">
          {t('error.error_info')}
        </Body>

        {authStore.isAuthenticated && (
          <LinkButton
            color="primary"
            icon="userOutlined"
            iconPosition="left"
            linkComponent={Link}
            to={ROUTES.LOGOUT}
          >
            {t('logout')}
          </LinkButton>
        )}

        <Button
          onClick={() => navigate(-1)}
          variant="outlined"
          icon="arrowLeft"
          iconPosition="left"
        >
          {t('go_back')}
        </Button>

        {isLocalOrTestEnv &&
          !!error &&
          typeof error === 'object' &&
          'message' in error &&
          typeof error.message === 'string' && (
            <>
              <hr className="my-3 w-full" />

              <code className="rounded bg-black-light p-3 text-left">
                {error.message}
              </code>
            </>
          )}
      </div>
    </>
  );
});
